import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { RevenueOpportunitiesSectionState } from 'anchor-scout-common/types';
import { BiContainer, Box, Button, Callout, Card, Collapse, Column, DropdownMenu, DropdownMenuItem, IconButton, Row, Skeleton, Spacing, SwipeArea, TextButton, Typography, useDevice } from 'seagull';
import { ChevronDown, ChevronUp } from 'seagull/icons/arrows';
import { getTestIds } from 'seagull/utils/TestIds';
import { SparkleIcon } from '~/components/ai/shared/SparkleIcon/SparkleIcon';
import { useLocaleKeys } from '~/i18n';
import { DeckStack } from './DeckStack/DeckStack';
import { useRevenueOpportunitiesSection } from './hooks/useRevenueOpportunitiesSection';
import { LearningDeck } from './LearningDeck/LearningDeck';
import { OpportunityDeck } from './OpportunityDeck/OpportunityDeck';
import { BUILDING_UP_KINDS, KIND_ICON, OPPORTUNITY_BI_CONTAINER, SKELETON_ROW_WIDTHS } from './RevenueOpportunitiesSection.constants';
import styles from './RevenueOpportunitiesSection.module.scss';
import { opportunityBiData } from './utils/opportunityCard';
export const GetRevenueOpportunitiesSectionTID = (parentTestId)=>getTestIds(parentTestId);
const DEFAULT_COLLAPSE_DURATION_MS = 600;
export const RevenueOpportunitiesSection = (param)=>{
    let { collapseDurationMs = DEFAULT_COLLAPSE_DURATION_MS } = param;
    const { RevenueOpportunitiesSectionTestIds: TID } = useMemo(()=>GetRevenueOpportunitiesSectionTID(), []);
    const { t: texts } = useLocaleKeys('ai.pulse.section');
    // eslint-disable-next-line no-restricted-syntax -- WHY: the section is the layout boundary; phones get the compact deck without the side list (frontend-mobile.md §3b)
    const { isMobileDevice } = useDevice();
    const { view, retry, hideForNow, lastSyncedDate, isCollapsed, toggleCollapsed, cardViews, frontIndex, showCard, showNextCard, actOnFrontCard, frontDismissOptions, isFrontDismissing, dismissFrontCard, swipeDeck } = useRevenueOpportunitiesSection(TID.Toasts);
    const frontCard = cardViews[frontIndex];
    if (view === 'nothing') {
        return null;
    }
    if (view === 'error') {
        return /*#__PURE__*/ _jsx(Callout, {
            intent: "error",
            testId: TID.Error.Root(),
            withAction: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'pulse_opportunities_retry'
                },
                testId: TID.Error.TryAgain(),
                skin: "monochrome",
                size: "small",
                onClick: retry,
                children: texts.retry()
            }),
            children: texts.error()
        });
    }
    return /*#__PURE__*/ _jsx(Card, {
        bi: {
            name: 'pulse_opportunities_section'
        },
        testId: TID.Root(),
        skin: "white",
        borderRadius: "l",
        autoHeight: true,
        disableSeparator: true,
        children: /*#__PURE__*/ _jsxs(Column, {
            padding: Spacing.Space16,
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    gap: Spacing.Space8,
                    alignItems: "start",
                    children: [
                        /*#__PURE__*/ _jsx(SparkleIcon, {
                            className: styles.sparkle
                        }),
                        /*#__PURE__*/ _jsx(Column, {
                            grow: true,
                            children: view === 'loading' ? /*#__PURE__*/ _jsxs(Column, {
                                gap: Spacing.Space4,
                                padding: {
                                    vertical: Spacing.Space2
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Skeleton, {
                                        width: 300,
                                        height: 24,
                                        className: styles.skeletonLine
                                    }),
                                    /*#__PURE__*/ _jsx(Skeleton, {
                                        width: 140,
                                        height: 12
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "h6",
                                        weight: "normal",
                                        skin: "secondary",
                                        testId: TID.Title(),
                                        children: texts.title()
                                    }),
                                    lastSyncedDate && /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        weight: "light",
                                        skin: "secondary",
                                        testId: TID.LastSynced(),
                                        children: texts.lastSynced({
                                            date: lastSyncedDate
                                        })
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(IconButton, {
                            bi: {
                                name: isCollapsed ? 'pulse_opportunities_expand' : 'pulse_opportunities_collapse'
                            },
                            testId: TID.CollapseToggle(),
                            "aria-label": isCollapsed ? texts.expand() : texts.collapse(),
                            "aria-expanded": !isCollapsed,
                            size: "small",
                            skin: "transparent",
                            noPadding: true,
                            onClick: toggleCollapsed,
                            children: isCollapsed ? /*#__PURE__*/ _jsx(ChevronDown, {
                                size: 24
                            }) : /*#__PURE__*/ _jsx(ChevronUp, {
                                size: 24
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Collapse, {
                    in: !isCollapsed,
                    timeout: collapseDurationMs,
                    unmountOnExit: true,
                    children: /*#__PURE__*/ _jsxs(Column, {
                        padding: {
                            top: Spacing.Space12
                        },
                        testId: TID.Content(),
                        children: [
                            view === 'loading' && /*#__PURE__*/ _jsxs(Box, {
                                className: cn(styles.deckAndList, {
                                    [styles.deckOnly]: isMobileDevice
                                }),
                                testId: TID.Loading(),
                                children: [
                                    /*#__PURE__*/ _jsxs(DeckStack, {
                                        border: "plain",
                                        compact: isMobileDevice,
                                        children: [
                                            /*#__PURE__*/ _jsxs(Row, {
                                                gap: Spacing.Space8,
                                                alignItems: "center",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Skeleton, {
                                                        width: 28,
                                                        height: 28
                                                    }),
                                                    /*#__PURE__*/ _jsx(Skeleton, {
                                                        width: 120,
                                                        height: 12
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(Skeleton, {
                                                width: 220,
                                                height: 36,
                                                className: styles.skeletonLine
                                            }),
                                            /*#__PURE__*/ _jsx(Skeleton, {
                                                width: 320,
                                                height: 20,
                                                className: styles.skeletonLine
                                            }),
                                            /*#__PURE__*/ _jsx(Skeleton, {
                                                width: 420,
                                                height: 16,
                                                className: styles.skeletonLine
                                            }),
                                            /*#__PURE__*/ _jsxs(Row, {
                                                gap: Spacing.Space8,
                                                alignItems: "center",
                                                className: styles.loadingActions,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Skeleton, {
                                                        width: 112,
                                                        height: 34
                                                    }),
                                                    /*#__PURE__*/ _jsx(Skeleton, {
                                                        width: 112,
                                                        height: 34
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    !isMobileDevice && /*#__PURE__*/ _jsxs(Column, {
                                        gap: Spacing.Space8,
                                        children: [
                                            /*#__PURE__*/ _jsx(Skeleton, {
                                                width: "100%",
                                                height: 72
                                            }),
                                            SKELETON_ROW_WIDTHS.map((width)=>/*#__PURE__*/ _jsxs(Row, {
                                                    gap: Spacing.Space8,
                                                    alignItems: "center",
                                                    padding: Spacing.Space8,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Skeleton, {
                                                            width: 28,
                                                            height: 28
                                                        }),
                                                        /*#__PURE__*/ _jsx(Box, {
                                                            grow: true,
                                                            children: /*#__PURE__*/ _jsx(Skeleton, {
                                                                width: width,
                                                                height: 14,
                                                                className: styles.skeletonLine
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx(Skeleton, {
                                                            width: 72,
                                                            height: 14
                                                        })
                                                    ]
                                                }, width))
                                        ]
                                    })
                                ]
                            }),
                            view === RevenueOpportunitiesSectionState.Done && /*#__PURE__*/ _jsxs(Column, {
                                alignItems: "center",
                                gap: Spacing.Space2,
                                padding: {
                                    vertical: Spacing.Space24,
                                    horizontal: Spacing.Space16
                                },
                                className: styles.doneBox,
                                testId: TID.Done.Root(),
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph1",
                                        weight: "normal",
                                        children: texts.done.title()
                                    }),
                                    /*#__PURE__*/ _jsxs(Typography, {
                                        variant: "paragraph2",
                                        weight: "light",
                                        skin: "secondary",
                                        children: [
                                            texts.done.body(),
                                            ' ',
                                            /*#__PURE__*/ _jsx(TextButton, {
                                                bi: {
                                                    name: 'pulse_opportunities_hide_for_now'
                                                },
                                                testId: TID.Done.HideForNow(),
                                                weight: "normal",
                                                underline: "none",
                                                variant: "paragraph3",
                                                className: styles.hideForNow,
                                                onClick: hideForNow,
                                                children: texts.done.hideForNow()
                                            })
                                        ]
                                    })
                                ]
                            }),
                            view === RevenueOpportunitiesSectionState.BuildingUp && /*#__PURE__*/ _jsxs(Box, {
                                padding: Spacing.Space24,
                                className: styles.buildingUp,
                                testId: TID.BuildingUp.Root(),
                                children: [
                                    /*#__PURE__*/ _jsxs(Column, {
                                        gap: Spacing.Space12,
                                        children: [
                                            /*#__PURE__*/ _jsxs(Column, {
                                                gap: Spacing.Space4,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        variant: "h6",
                                                        weight: "normal",
                                                        children: texts.buildingUp.title()
                                                    }),
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        variant: "paragraph2",
                                                        weight: "light",
                                                        skin: "secondary",
                                                        children: texts.buildingUp.body()
                                                    })
                                                ]
                                            }),
                                            !isMobileDevice && /*#__PURE__*/ _jsx(Column, {
                                                gap: Spacing.Space2,
                                                testId: TID.BuildingUp.Types(),
                                                children: BUILDING_UP_KINDS.map((kind)=>/*#__PURE__*/ _jsxs(Row, {
                                                        gap: Spacing.Space8,
                                                        alignItems: "start",
                                                        padding: {
                                                            vertical: Spacing.Space8,
                                                            horizontal: Spacing.Space4
                                                        },
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Box, {
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                className: styles.learningRing,
                                                                "aria-hidden": true
                                                            }),
                                                            /*#__PURE__*/ _jsxs(Column, {
                                                                gap: Spacing.Space2,
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Typography, {
                                                                        variant: "paragraph1",
                                                                        weight: "normal",
                                                                        children: texts.buildingUp.types[kind].title()
                                                                    }),
                                                                    /*#__PURE__*/ _jsx(Typography, {
                                                                        variant: "paragraph2",
                                                                        weight: "light",
                                                                        skin: "secondary",
                                                                        children: texts.buildingUp.types[kind].line()
                                                                    })
                                                                ]
                                                            })
                                                        ]
                                                    }, kind))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(LearningDeck, {
                                        compact: isMobileDevice
                                    })
                                ]
                            }),
                            view === RevenueOpportunitiesSectionState.Active && frontCard && /*#__PURE__*/ _jsxs(Box, {
                                className: cn(styles.deckAndList, {
                                    [styles.deckOnly]: isMobileDevice
                                }),
                                children: [
                                    /*#__PURE__*/ _jsx(BiContainer, {
                                        name: OPPORTUNITY_BI_CONTAINER,
                                        data: opportunityBiData(frontCard, frontIndex),
                                        children: /*#__PURE__*/ _jsx(SwipeArea, {
                                            bi: {
                                                name: 'pulse_opportunities_deck_swipe'
                                            },
                                            testId: TID.DeckSwipe(),
                                            onSwipe: swipeDeck,
                                            children: /*#__PURE__*/ _jsx(OpportunityDeck, {
                                                compact: isMobileDevice,
                                                card: frontCard,
                                                onAction: actOnFrontCard,
                                                onNext: showNextCard,
                                                isBusy: isFrontDismissing,
                                                dismiss: /*#__PURE__*/ _jsx(DropdownMenu, {
                                                    hAlign: "left",
                                                    mobile: {
                                                        title: texts.amountPerYear({
                                                            amount: frontCard.amount
                                                        }),
                                                        subtitle: frontCard.lead
                                                    },
                                                    target: /*#__PURE__*/ _jsx(Button, {
                                                        bi: {
                                                            name: 'pulse_opportunities_dismiss_open'
                                                        },
                                                        testId: TID.Dismiss.Button(),
                                                        skin: "primaryOutline",
                                                        size: isMobileDevice ? 'xsmall' : 'small',
                                                        loading: isFrontDismissing,
                                                        suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {
                                                            size: 16
                                                        }),
                                                        children: texts.dismiss()
                                                    }),
                                                    children: frontDismissOptions.map((param)=>{
                                                        let { option, label } = param;
                                                        return /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                            bi: {
                                                                name: "pulse_opportunities_dismiss_".concat(option)
                                                            },
                                                            testId: TID.Dismiss.Option(option),
                                                            onClick: ()=>dismissFrontCard(option),
                                                            children: label
                                                        }, option);
                                                    })
                                                })
                                            })
                                        })
                                    }),
                                    !isMobileDevice && /*#__PURE__*/ _jsx(Column, {
                                        padding: {
                                            right: Spacing.Space4,
                                            bottom: Spacing.Space16
                                        },
                                        className: styles.sideList,
                                        testId: TID.SideList.Root(),
                                        children: cardViews.map((cardView, index)=>{
                                            const KindIcon = KIND_ICON[cardView.kind];
                                            const isCurrent = index === frontIndex;
                                            return /*#__PURE__*/ _jsx(BiContainer, {
                                                name: OPPORTUNITY_BI_CONTAINER,
                                                data: opportunityBiData(cardView, index),
                                                children: /*#__PURE__*/ _jsx(Card, {
                                                    as: "button",
                                                    bi: {
                                                        name: 'pulse_opportunities_side_list_row'
                                                    },
                                                    testId: TID.SideList.Row(index),
                                                    "aria-current": isCurrent ? 'true' : 'false',
                                                    skin: isCurrent ? 'mobileGray' : 'white',
                                                    borderRadius: "m",
                                                    shadow: "none",
                                                    className: cn(styles.sideListRow, {
                                                        [styles.current]: isCurrent
                                                    }),
                                                    onClick: ()=>showCard(index),
                                                    children: /*#__PURE__*/ _jsxs(Row, {
                                                        gap: Spacing.Space8,
                                                        alignItems: "center",
                                                        padding: Spacing.Space8,
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Box, {
                                                                inline: true,
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                className: styles.sideListIcon,
                                                                children: /*#__PURE__*/ _jsx(KindIcon, {
                                                                    size: 16
                                                                })
                                                            }),
                                                            /*#__PURE__*/ _jsx(Column, {
                                                                grow: true,
                                                                children: /*#__PURE__*/ _jsx(Typography, {
                                                                    variant: "paragraph1",
                                                                    weight: isCurrent ? 'normal' : 'light',
                                                                    ellipsis: true,
                                                                    children: cardView.rowTitle
                                                                })
                                                            }),
                                                            /*#__PURE__*/ _jsxs(Typography, {
                                                                variant: "paragraph1",
                                                                weight: isCurrent ? 'normal' : 'light',
                                                                className: styles.rowAmount,
                                                                children: [
                                                                    cardView.rowAmount,
                                                                    /*#__PURE__*/ _jsx(Typography, {
                                                                        as: "span",
                                                                        variant: "paragraph2",
                                                                        weight: isCurrent ? 'normal' : 'light',
                                                                        skin: "secondary",
                                                                        children: texts.perYear()
                                                                    })
                                                                ]
                                                            })
                                                        ]
                                                    })
                                                })
                                            }, cardView.identity);
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                })
            ]
        })
    });
};
